import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Store } from '@ngrx/store';

import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { AppState } from '../main-module/state';
import { loadTemplatePipeline } from '../newsletters-module/state/actions/template-pipeline.actions';

import { ABTest, ABTestParameters, ABTestVariant } from '../newsletters-module/declarations/ab-test';
import { RecommendationPipeline } from '../newsletters-module/declarations/pipeline/recommendation-pipeline';
import { EditorialChoices, TemplateContent } from '../providers/template.provider';

import { environment } from '../../environments/environment';

/**
 * Gestion d'un test A/B sur une newsletter (E5). L'appel HTTP est fait ici, pas dans un effect NgRx.
 * Les appels retournent simplement le test sans dispatch au state (la sélection du test à éditer
 * est gérée séparément via abTestEditingSelection).
 */
@Injectable({
  providedIn: 'root'
})
export class ABTestService {

  // Coalescence des requêtes concurrentes à `listABTests` pour une même source : le guard de route et
  // `AbTestComponent` peuvent tous deux en déclencher une au chargement initial de l'onglet (le guard ne les
  // attend pas avant de laisser la navigation continuer) — sans ce cache, ça part en 2 requêtes HTTP identiques.
  private readonly listABTestsInFlight = new Map<string, Promise<ABTest[]>>();

  constructor(
    private http: HttpClient,
    private store: Store<AppState>
  ) { }

  private basePath(engineId: number, sourceId: string): string {
    return `${environment.apiUrl2}/2.0/engines/${engineId}/sources/${sourceId}/ab-tests`;
  }

  createABTest(engineId: number, sourceId: string, params: ABTestParameters): Promise<ABTest> {
    const test$ = this.http.post<ABTest>(this.basePath(engineId, sourceId), { ...params });

    return firstValueFrom(test$);
  }

  // Aussi utilisée pour l'arrêt anticipé d'un test : pas d'endpoint dédié, juste une mise à jour avec la date de
  // fin ramenée à maintenant.
  updateABTest(engineId: number, sourceId: string, testId: string, params: ABTestParameters): Promise<ABTest> {
    const test$ = this.http.put<ABTest>(`${this.basePath(engineId, sourceId)}/${testId}`, { ...params });

    return firstValueFrom(test$);
  }

  // Contenu de la version B, décorrélé du protocole (endpoint dédié) : remplace tout le contenu existant, pas une
  // mise à jour partielle — le body doit donc porter l'état complet de la variante, pas seulement les champs
  // modifiés.
  updateABTestVariant(engineId: number, sourceId: string, testId: string, variant: ABTestVariant): Promise<ABTest> {
    const test$ = this.http.put<ABTest>(`${this.basePath(engineId, sourceId)}/${testId}/variant`, { ...variant });

    return firstValueFrom(test$);
  }

  // Pipeline de recommandation de la variante B, accès dédié : volontairement non tagué comme celui de la source
  // live, donc invisible pour l'endpoint générique de pipeline. À utiliser à la place dès que l'édition porte sur
  // B, pas sur A.
  getABTestVariantPipeline(engineId: number, sourceId: string, testId: string): Promise<RecommendationPipeline> {
    return firstValueFrom(this.getABTestVariantPipeline$(engineId, sourceId, testId));
  }

  // Version Observable, qui dispatch aussi la mise à jour du pipeline courant dans le state NgRx : nécessaire pour
  // les appelants dont le pipeline transite par le state plutôt que par une variable locale de composant.
  getABTestVariantPipeline$(engineId: number, sourceId: string, testId: string): Observable<RecommendationPipeline> {
    return this.http.get<RecommendationPipeline>(`${this.basePath(engineId, sourceId)}/${testId}/variant/pipeline`)
      .pipe(
        map((pipeline) => {
          this.store.dispatch(loadTemplatePipeline({ pipeline }));
          return pipeline;
        })
      );
  }

  updateABTestVariantPipeline(engineId: number, sourceId: string, testId: string, pipeline: RecommendationPipeline): Promise<RecommendationPipeline> {
    return firstValueFrom(this.http.put<RecommendationPipeline>(`${this.basePath(engineId, sourceId)}/${testId}/variant/pipeline`, pipeline));
  }

  // Contenu éditorial (recommandations + sujet) de la variante B, accès dédié : même raisonnement que pour le
  // pipeline, non résolu par les endpoints génériques de la source live. La forme de retour est volontairement
  // identique à celle de l'endpoint éditorial existant, pour rester réutilisable telle quelle.
  getABTestVariantEditorial(engineId: number, sourceId: string, testId: string): Promise<EditorialChoices> {
    return firstValueFrom(this.http.get<EditorialChoices>(`${this.basePath(engineId, sourceId)}/${testId}/variant/editorial`));
  }

  // Remplace tout le contenu éditorial (sujet + recommandations ensemble, pas de fusion partielle côté backend) :
  // contrairement à la source live où ce sont deux endpoints indépendants, l'appelant doit toujours fournir les
  // deux champs, y compris celui qu'il n'a pas modifié.
  updateABTestVariantEditorial(
    engineId: number, sourceId: string, testId: string, editorial: { subject: string | null; recommendations: TemplateContent }
  ): Promise<ABTest> {
    const test$ = this.http.put<ABTest>(`${this.basePath(engineId, sourceId)}/${testId}/variant/editorial`, editorial);

    return firstValueFrom(test$);
  }

  // Historique complet de la source, terminés compris (utilisé en E5 seulement pour détecter un test déjà
  // programmé dans le futur ; l'affichage de l'historique lui-même relève du lot 2, E9).
  //
  // Coalescé : deux appels concurrents pour la même source (engineId, sourceId) partagent la même requête HTTP en
  // vol plutôt que d'en émettre une chacun. Un appel postérieur à la résolution du précédent déclenche bien une
  // nouvelle requête (pas de cache de résultat, juste de la déduplication de requêtes concurrentes).
  listABTests(engineId: number, sourceId: string): Promise<ABTest[]> {
    const key = `${engineId}:${sourceId}`;

    const inFlight = this.listABTestsInFlight.get(key);
    if (inFlight) return inFlight;

    const request = firstValueFrom(this.http.get<ABTest[]>(this.basePath(engineId, sourceId)))
      .finally(() => this.listABTestsInFlight.delete(key));

    this.listABTestsInFlight.set(key, request);
    return request;
  }
}
